import { useEffect, useRef } from 'react';

import debug from 'debug';
import { reaction } from 'mobx';

import { getLocalStorage, useAppConfig } from '@r-client/shared/util/core';

import {
  IGetFeatureToggleQueryOpts,
  useGetFeatureToggleQuery as useGetFeatureToggleQueryOriginal,
} from '../graphql/get-feature-toggle-query';
import { IGetFeatureToggleQuery } from '../graphql/types';

const log = debug('data:flags:cached');

const STORAGE_PREFIX = 'flag_';
const FORCE_PREFIX = 'force:';

function getStorageKey(feature: string): string {
  return `${STORAGE_PREFIX}${feature}`;
}

function readFromLocalStorage(key: string): {
  enabled: boolean | null;
  hasForcePrefix: boolean;
} {
  const storage = getLocalStorage();
  if (!storage) {
    return { enabled: null, hasForcePrefix: false };
  }

  try {
    const value = storage.getItem(key);
    if (!value) {
      return { enabled: null, hasForcePrefix: false };
    }

    const hasForcePrefix = value.startsWith(FORCE_PREFIX);
    const cleanValue = hasForcePrefix
      ? value.slice(FORCE_PREFIX.length)
      : value;

    // Parse as boolean (handles "true", "false")
    // Return null for invalid values to trigger fallback to server
    if (cleanValue !== 'true' && cleanValue !== 'false') {
      log(`Invalid feature flag value in localStorage: ${cleanValue}`);
      return { enabled: null, hasForcePrefix: false };
    }

    const enabled = cleanValue === 'true';

    return { enabled, hasForcePrefix };
  } catch (error) {
    log(`Failed to read feature flag from localStorage:`, error);
    return { enabled: null, hasForcePrefix: false };
  }
}

function writeToLocalStorage(key: string, enabled: boolean): void {
  const storage = getLocalStorage();
  if (!storage) {
    return;
  }

  try {
    storage.setItem(key, `${enabled}`);
  } catch (error) {
    log(`Failed to write feature flag to localStorage:`, error);
  }
}

const ENVS_USING_LOCAL_STORAGE = ['development', 'staging'];

export function useGetFeatureToggleQuery(
  opts: Omit<IGetFeatureToggleQueryOpts, 'client'>
) {
  const originalQueryResult = useGetFeatureToggleQueryOriginal(opts);
  const appConfig = useAppConfig();

  // Read localStorage only once during initialization
  const configRef = useRef(() => {
    const applicationEnvironment =
      appConfig?.applicationEnvironment || 'production';
    // Disable localStorage usage in production
    // eslint-disable-next-line no-process-env
    const isLocalStorageEnabled = ENVS_USING_LOCAL_STORAGE.includes(
      applicationEnvironment
    );
    const feature = opts.variables.feature;
    const storageKey = getStorageKey(feature);
    // Only read from localStorage in development mode
    // In production/staging, ignore localStorage completely
    const { enabled: cachedEnabled, hasForcePrefix } = isLocalStorageEnabled
      ? readFromLocalStorage(storageKey)
      : { enabled: null, hasForcePrefix: false };

    return {
      isLocalStorageEnabled,
      storageKey,
      hasForcePrefix,
      cachedEnabled,
    };
  });

  const config = configRef.current();
  const currentFlagValueRef = useRef<boolean | null>(config.cachedEnabled);
  const previousDataRef = useRef<typeof originalQueryResult.data>();

  useEffect(
    () =>
      reaction(
        () => ({
          data: originalQueryResult.data,
          isLoading: originalQueryResult.isLoading,
        }),
        ({ data, isLoading }) => {
          if (isLoading || !data?.flipper || config.hasForcePrefix) {
            return;
          }

          const newValue = data.flipper.enabled;
          currentFlagValueRef.current = newValue;

          // Write to localStorage in development (non-blocking)
          if (config.isLocalStorageEnabled) {
            writeToLocalStorage(config.storageKey, newValue);
          }
        }
      ),
    // Config values are computed once on mount and never change, safe to omit from deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  // Update cache when data changes (for testing compatibility)
  useEffect(() => {
    if (
      !originalQueryResult.isLoading &&
      originalQueryResult.data?.flipper &&
      !config.hasForcePrefix &&
      previousDataRef.current !== originalQueryResult.data
    ) {
      const newValue = originalQueryResult.data.flipper.enabled;
      currentFlagValueRef.current = newValue;

      if (config.isLocalStorageEnabled) {
        writeToLocalStorage(config.storageKey, newValue);
      }

      previousDataRef.current = originalQueryResult.data;
    }
  }, [
    originalQueryResult.isLoading,
    originalQueryResult.data,
    config.hasForcePrefix,
    config.isLocalStorageEnabled,
    config.storageKey,
  ]);

  if (originalQueryResult.isLoading) {
    return originalQueryResult;
  }

  // Use cached value if available, otherwise fall back to server value
  const effectiveEnabled =
    currentFlagValueRef.current !== null
      ? currentFlagValueRef.current
      : originalQueryResult.data?.flipper?.enabled;

  return {
    ...originalQueryResult,
    isLoading: originalQueryResult.isLoading,
    data: {
      flipper: {
        ...originalQueryResult.data?.flipper,
        enabled: effectiveEnabled,
      },
    } as IGetFeatureToggleQuery,
  };
}
